import React, { useEffect, useState } from "react";
import { isAvatarLoaded, markAvatarLoaded } from "../helper/avatarCache";
import "./GroupAvatarImage.css";

/**
 * GroupAvatarImage
 * Renders a chat group / room avatar image with a polished loading UI.
 *
 * - When the image bytes are already in the browser cache (e.g. because the
 *   chat list preloaded them via `helper/avatarCache`), the image is
 *   rendered fully opaque on the first paint – no skeleton, no fade-in.
 * - When the image is not yet cached, a shimmer skeleton is shown until it
 *   loads, then the image fades in smoothly.
 * - On error (or when no `src` is provided): renders the existing initials
 *   fallback (`.project-avatar.project-avatar-outline`) using the supplied
 *   theme color, matching the rest of the chat list UI.
 *
 * The component intentionally re-uses the same `.project-avatar` class so
 * the size / shape stays consistent with surrounding list items.
 */
const GroupAvatarImage = ({
  src,
  alt = "",
  initials = "",
  themeColor = "#737373",
  className = "",
  style,
  imgStyle,
}) => {
  const [isLoaded, setIsLoaded] = useState(() => isAvatarLoaded(src));
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setIsLoaded(isAvatarLoaded(src));
    setHasError(false);
  }, [src]);

  if (!src || hasError) {
    return (
      <div
        className={`project-avatar project-avatar-outline ${className}`.trim()}
        style={{ "--lm-project-color": themeColor || "#737373", ...style }}
      >
        <div className="project-avatar-spacer" />
        {initials}
        <div className="project-avatar-spacer" />
      </div>
    );
  }

  return (
    <div
      className={`project-avatar group-avatar-loader ${isLoaded ? "is-loaded" : "is-loading"} ${className}`.trim()}
      style={{ "--lm-project-color": themeColor || "#737373", ...style }}
    >
      {!isLoaded && (
        <div
          className="group-avatar-skeleton"
          aria-hidden="true"
        />
      )}
      <img
        src={src}
        alt={alt}
        className="group-avatar-img group-avatar-image__img"
        decoding="async"
        ref={(node) => {
          if (node && node.complete && node.naturalHeight !== 0) {
            markAvatarLoaded(src);
            if (!isLoaded) setIsLoaded(true);
          }
        }}
        style={imgStyle}
        onLoad={() => {
          markAvatarLoaded(src);
          setIsLoaded(true);
        }}
        onError={() => {
          setHasError(true);
          setIsLoaded(false);
        }}
      />
    </div>
  );
};

export default GroupAvatarImage;
